import { useState } from "react";
import { useNavigate } from "react-router-dom";
import type { ColumnDef, SortingState } from "@tanstack/react-table";
import type { InvoiceAgingBucket } from "@adva/types";
import { useInvoices } from "@/hooks/use-api";
import { ListPage } from "@/components/v2/list-page";
import { DataTable } from "@/components/v2/data-table";
import { EmptyState } from "@/components/v2/empty-state";
import { InvoicesAgingView } from "@/components/v2/invoices-aging-view";
import { ViewSwitcher } from "@/components/v2/view-switcher";
import { AggregateStrip } from "@/components/v2/aggregate-strip";
import { FilterToolbar } from "@/components/v2/filter-toolbar";
import { FilterChip } from "@/components/v2/filter-chip";
import { SearchHint } from "@/components/v2/search-hint";
import { StatusBadge } from "@/components/status-badge";
import { Button } from "@/components/ui/button";
import { formatCurrency, formatDate } from "@/lib/utils";
import { LIST_PAGE_VIEWS } from "@/pages/v2/_shared/list-page-views";
import { formatShowingRange } from "@/pages/v2/_shared/format-showing-range";

type Row = Record<string, unknown>;

const BUCKET_LABELS: Record<InvoiceAgingBucket, string> = {
  current: "Current",
  days_31_60: "31 – 60 days",
  days_61_90: "61 – 90 days",
  days_90_plus: "90+ days",
};

const columns: ColumnDef<Row, unknown>[] = [
  {
    accessorKey: "invoice_number",
    header: "Invoice #",
    cell: ({ row }) => (
      <span className="typo-mono font-medium">
        {(row.original.display_id as string) ||
          (row.original.invoice_number as string) ||
          "—"}
      </span>
    ),
  },
  {
    accessorKey: "customer_name",
    header: "Customer",
    cell: ({ row }) => {
      const name = row.original.customer_name as string | undefined;
      return (
        <span className={name ? "" : "text-[color:var(--color-text-lo)]"}>
          {name || "—"}
        </span>
      );
    },
  },
  {
    accessorKey: "assigned_rep_name",
    header: "Rep",
    cell: ({ row }) => {
      const rep = row.original.assigned_rep_name as string | undefined;
      return (
        <span className={rep ? "" : "text-[color:var(--color-text-lo)]"}>
          {rep || "—"}
        </span>
      );
    },
  },
  {
    accessorKey: "total_amount",
    header: "Amount",
    meta: { numeric: true },
    cell: ({ row }) => (
      <span className="font-medium">
        {formatCurrency(row.original.total_amount as number)}
      </span>
    ),
  },
  {
    accessorKey: "balance_due",
    header: "Balance Due",
    meta: { numeric: true },
    cell: ({ row }) => {
      const bal = row.original.balance_due as number | null;
      return (
        <span
          className={
            bal && bal > 0
              ? "text-[color:var(--color-danger)] font-medium"
              : "text-[color:var(--color-text-md)]"
          }
        >
          {bal != null ? formatCurrency(bal) : "—"}
        </span>
      );
    },
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => <StatusBadge status={row.original.status as string} />,
  },
  {
    accessorKey: "due_date",
    header: "Date",
    cell: ({ row }) => (
      <span className="text-[color:var(--color-text-md)]">
        {formatDate(row.original.due_date as string)}
      </span>
    ),
  },
];

const VIEWS = LIST_PAGE_VIEWS.invoices;

export default function V2InvoicesPage() {
  const navigate = useNavigate();
  const [view, setView] = useState<string>(VIEWS.defaultValue);
  const [agingBucket, setAgingBucket] = useState<
    InvoiceAgingBucket | undefined
  >(undefined);
  const [sorting, setSorting] = useState<SortingState>([
    { id: "created_at", desc: true },
  ]);
  const [pageIndex, setPageIndex] = useState(0);
  const pageSize = 50;

  const { data, isLoading, isError, refetch } = useInvoices({
    offset: pageIndex * pageSize,
    limit: pageSize,
    sort_by: sorting[0]?.id,
    sort_order: sorting[0]?.desc ? "desc" : "asc",
    aging_bucket: agingBucket,
  });

  const invoices = (data?.invoices ?? []) as Row[];
  const total = data?.total ?? 0;

  function handleBucketSelect(bucket: InvoiceAgingBucket) {
    setAgingBucket(bucket);
    setPageIndex(0);
    setView("table");
  }

  function clearAgingFilter() {
    setAgingBucket(undefined);
    setPageIndex(0);
  }

  // TODO(ADV-787-followup): Outstanding / Overdue / Collected cells need a
  // DB-side aggregate view. Until that lands we render `—` rather than
  // .reduce() across the page.
  const aggregateItems = [
    {
      label: "Showing",
      value: formatShowingRange(invoices.length, pageIndex, pageSize, total),
    },
    { label: "Outstanding", value: "—" },
    { label: "Overdue", value: "—" },
    { label: "Collected", value: "—" },
  ];

  return (
    <ListPage
      title="Invoices"
      actions={
        <>
          <SearchHint />
          <Button onClick={() => navigate("/invoices/new")}>
            Create invoice
          </Button>
        </>
      }
      tabs={
        <ViewSwitcher
          value={view}
          onValueChange={setView}
          options={[...VIEWS.options]}
          aria-label="Invoices view"
        />
      }
      filters={
        <FilterToolbar entity="invoices">
          {agingBucket && (
            <FilterChip
              label="Aging"
              value={BUCKET_LABELS[agingBucket]}
              active
              onDismiss={clearAgingFilter}
            />
          )}
        </FilterToolbar>
      }
      aggregateStrip={
        view === "table" ? (
          <AggregateStrip
            items={aggregateItems}
            sort={{ label: "Created", direction: "desc" }}
          />
        ) : undefined
      }
    >
      {view === "aging" && (
        <InvoicesAgingView onBucketSelect={handleBucketSelect} />
      )}
      {view === "table" && (
        <DataTable
          columns={columns}
          data={invoices}
          total={total}
          isLoading={isLoading}
          isError={isError}
          errorEntityLabel="invoices"
          onRetry={() => refetch()}
          onRowClick={(row) => navigate(`/invoices/${row.id}`)}
          emptyMessage="No invoices"
          emptyDescription={
            agingBucket
              ? `No invoices in the ${BUCKET_LABELS[agingBucket]} bucket. Clear the filter to see all invoices.`
              : "Invoices appear here once a job is completed and an invoice is issued."
          }
          pageSize={pageSize}
          pageIndex={pageIndex}
          onPaginationChange={setPageIndex}
          sorting={sorting}
          onSortingChange={setSorting}
        />
      )}
      {view === "analytics" && (
        <EmptyState
          title="Analytics ships in Wave 2"
          description="The Invoices analytics surface is part of the broader operational-screens plan."
        />
      )}
    </ListPage>
  );
}
